// src/components/services/ProtectedRoute.tsx
import React, {useEffect, useState, useCallback} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useTelegramContext} from './telegramContext';
import {apiService} from './api_url';
import {useAppStore} from '../store/useAppStore';
import Loader from '../widgets/Loader/Loader';

interface ProtectedRouteProps {
    children: React.ReactNode;
    fallbackPath?: string;
    useUnauthorizedPage?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
                                                           children,
                                                           fallbackPath = '/unauthorized',
                                                           useUnauthorizedPage = true
                                                       }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [authCheckError, setAuthCheckError] = useState<string | null>(null);
    const [userNotInDb, setUserNotInDb] = useState<boolean>(false);
    const {isTelegram, user, isReady, showAlert, closeApp} = useTelegramContext();
    const {
        isAuthenticated,
        setIsAuthenticated,
        setTelegramId,
        loadUserData,
        setAuthChecked
    } = useAppStore();

    const location = useLocation();

    const checkAuth = useCallback(async () => {
        if (!isTelegram) {
            setIsLoading(false);
            return;
        }
        // ТОЛЬКО ДЛЯ TELEGRAM: проверяем, существует ли пользователь в БД
        if (isTelegram && user) {
            const tgId = user.id.toString();

            setUserNotInDb(false);
            setAuthCheckError(null);

            try {
                const userData = await loadUserData(tgId);

                if (!userData) {
                    // Пользователь не найден в БД
                    setUserNotInDb(true);
                    setIsLoading(false);

                    // Показываем алерт
                    const alertMessage = '⚠️ Вы не зарегистрированы в системе!\n\nПожалуйста, сначала запустите бота и нажмите /start, чтобы зарегистрироваться.';

                    if (showAlert) {
                        showAlert(alertMessage);
                    } else {
                        alert(alertMessage);
                    }

                    // Закрываем приложение через 3 секунды
                    setTimeout(() => {
                        if (closeApp) {
                            closeApp();
                        }
                    }, 3000);

                    return;
                }

                // ✅ Пользователь существует в БД
                setIsAuthenticated(true);
                setTelegramId(tgId);
                // ⚠️ ВАЖНО: loadUserData уже сохраняет данные в store внутри себя
                // Поэтому дополнительно вызывать setUserData не нужно
                setAuthCheckError(null);
                setIsLoading(false);
                return;
            } catch (error) {
                console.error('Error checking user in DB:', error);
                setUserNotInDb(false);
                setAuthCheckError('Ошибка проверки регистрации');
                setIsLoading(false);
                return;
            }
        }

        // ДЛЯ САЙТА (не Telegram): проверяем через куки как и было
        // try {
        //     const authCheck = await apiService.checkAuth();
        //
        //     if (authCheck.status === 'authenticated') {
        //         setIsAuthenticated(true);
        //         setTelegramId(authCheck.user.telegram_id);
        //         // ✅ Загружаем данные пользователя для сайта
        //         await loadUserData(authCheck.user.telegram_id);
        //         setAuthCheckError(null);
        //         setAuthChecked(true);
        //         setIsLoading(false);
        //         return;
        //     }
        //
        //     if (authCheck.status === 'unauthorized') {
        //         setIsAuthenticated(false);
        //         setAuthCheckError(null);
        //         setAuthChecked(true);
        //         setIsLoading(false);
        //         return;
        //     }
        //
        //     if (authCheck.status === 'error') {
        //         setAuthCheckError(authCheck.message);
        //         setAuthChecked(true);
        //         setIsLoading(false);
        //         return;
        //     }
        // } catch (error) {
        //     console.error('Auth check error:', error);
        //     setIsAuthenticated(false);
        //     setAuthCheckError('Не удалось проверить авторизацию');
        //     setAuthChecked(true);
        //     setIsLoading(false);
        // }
    }, [isTelegram, user, setIsAuthenticated, setTelegramId, loadUserData, setAuthChecked, showAlert, closeApp]);

    useEffect(() => {
        if (isReady) {
            checkAuth();
        }
    }, [isReady, checkAuth]);

    // Если пользователь не найден в БД (только для Telegram)
    if (userNotInDb) {
        return (
            <div className="tg-app">
                <div className="profile-page loading">
                    <div className="loader"><Loader/></div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        if (!isTelegram) {
            return null;
        }

        return (
            <div className="tg-app">
                <div className="profile-page loading">
                    <div className="loader"><Loader/></div>
                </div>
            </div>
        );
    }

    if (authCheckError && !isAuthenticated) {
        return (
            <div className="tg-app">
                <div className="profile-page loading">
                    <div style={{textAlign: 'center', padding: '20px', color: '#ff8888'}}>
                        {authCheckError}
                        <br/>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                marginTop: '16px',
                                padding: '8px 16px',
                                background: '#0B3D3F',
                                border: '1px solid #5CFFE1',
                                borderRadius: '8px',
                                color: '#5CFFE1',
                                cursor: 'pointer'
                            }}
                        >
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        if (useUnauthorizedPage) {
            return <Navigate to="/unauthorized" state={{from: location}} replace/>;
        }
        return <Navigate to={fallbackPath} state={{from: location}} replace/>;
    }

    return <>{children}</>;
};

export default ProtectedRoute;